/*
 *  KAN_Logo3DGPU.js
 *  Project: Kanoogi Platform — the GPU half of KAN_Logo3D
 *
 *  The site logos are PLATFORM class (docs/webgpu-conversion-plan.md §0, §7): they try
 *  WebGPU first, then WebGL, then leave the <img>. KAN_Logo3D.js owns everything the tiers
 *  share - OBJ parsing, BuildMesh, the animation, the fit-to-placeholder camera, the
 *  watchdog - and it creates the contexts. This file is the part that FORKS: the two
 *  shaders, buffer upload and the draw calls, one backend per tier, behind one interface:
 *
 *      backend.resize(W, H)               backing store is W x H device pixels
 *      backend.begin(frame)               frame = FrameValues(lighting, proj): the per-frame uniforms
 *      backend.unit(ui, mv, nm, ranges)   one animation unit: its modelView, normal matrix
 *                                         and mesh.objects index ranges
 *      backend.end()                      submit the frame
 *      backend.dispose()                  free every GPU object this backend made
 *
 *  🔴 Two shaders, one look. The Blinn-Phong lighting exists in GLSL (the WebGL tier, which
 *  is the pre-WebGPU renderer kept verbatim) AND in WGSL, and which tier a visitor gets must
 *  not change how the logo looks. The WGSL is written statement-for-statement against the
 *  GLSL, and tools/testing/logo-render-parity.mjs normalises both and holds them equal: move
 *  one constant in one shader and it goes red. The uniforms come from ONE table
 *  (FRAME_FIELDS / UNIT_FIELDS) and ONE function (FrameValues), so a page's lighting preset
 *  reaches both tiers through the same code.
 *
 *  No imports, on purpose: tests import this file under Node (KAN_Logo3D.js imports
 *  gl-matrix, which Node cannot resolve from Kanlibs).
 *
 *  Copyright © 2026 Kanoogi Inc. | All Rights Reserved
 */

"use strict";

/* ---------- Shaders ---------- */

// WebGL tier. Verbatim from KAN_Logo3D.js before the WebGPU tier existed (af1530c0b).
export const LOGO_GLSL_VS=`
attribute vec3 aPos;
attribute vec3 aNormal;
attribute vec3 aColor;
uniform mat4 uProj;
uniform mat4 uModelView;
uniform mat3 uNormalMatrix;
uniform vec3 uPointLightPosObj;		// object-space position of the optional point light (about/forums swipe)
varying vec3 vNormal;
varying vec3 vColor;
varying vec3 vPosView;
varying vec3 vPLPosView;			// view-space position of the point light, fed to fragment shader
void main()
	{
	vec4 viewPos=uModelView*vec4(aPos,1.0);
	vPosView=viewPos.xyz;
	vNormal=uNormalMatrix*aNormal;
	vColor=aColor;
	vPLPosView=(uModelView*vec4(uPointLightPosObj,1.0)).xyz;	// same mv each pixel — interpolation harmless
	gl_Position=uProj*viewPos;
	}
`;

export const LOGO_GLSL_FS=`
precision mediump float;
varying vec3 vNormal;
varying vec3 vColor;
varying vec3 vPosView;
varying vec3 vPLPosView;
uniform vec3 uLightDir;
uniform vec3 uLightColor;	// directional+specular tint; default (1,1,1) = identical to old white-light shader
uniform float uAmbient;
uniform float uSpecStrength;
uniform float uShininess;
uniform vec3 uPointLightColor;		// colour of the optional point light
uniform float uPointLightIntensity;	// 0 disables — multiplies the whole point contribution to zero
uniform float uPointLightFalloff;	// coefficient in 1/(1+d²·k); tuned per-mesh since object-space units vary
void main()
	{
	vec3 N=normalize(vNormal);
	vec3 L=normalize(uLightDir);
	float lambert=max(dot(N,L),0.0);
	float diffuse=(1.0-uAmbient)*lambert;
	vec3 V=normalize(-vPosView);
	vec3 H=normalize(L+V);
	float spec=pow(max(dot(N,H),0.0),uShininess)*uSpecStrength;
	// Ambient term stays uncoloured so face-away pixels keep the model's
	// intrinsic colours. Only the diffuse + specular contributions pick
	// up uLightColor — so a red light tints the LIT side of the logo.
	vec3 color=vColor*(uAmbient+diffuse*uLightColor)+vec3(spec)*uLightColor;
	// Optional point light — view-space position interpolated from the
	// vertex shader, so it's a single per-frame position. The per-pixel
	// vector toLight differs across the surface even on a coplanar face
	// (because vPosView varies), which is what produces the visible
	// "spot" dancing across the front of the logo — the thing the
	// directional-light variant of the swipe couldn't do. Falloff is
	// inverse-square-ish (1/(1+d²)). uPointLightIntensity=0 → zero
	// contribution → other pages (home, joinbeta, BEGIN) unaffected.
	vec3 toLight=vPLPosView-vPosView;
	float dist=length(toLight);
	vec3 PL=toLight/max(dist,0.0001);
	float pLambert=max(dot(N,PL),0.0);
	float falloff=1.0/(1.0+dist*dist*uPointLightFalloff);
	vec3 H2=normalize(PL+V);
	float pSpec=pow(max(dot(N,H2),0.0),uShininess)*uSpecStrength;
	color+=(vColor*pLambert+vec3(pSpec))*uPointLightColor*falloff*uPointLightIntensity;
	gl_FragColor=vec4(color,1.0);
	}
`;

// WebGPU tier. Each statement mirrors the GLSL above, in the same order and with the same
// names; only the declarations and the struct prefixes (f. u. in. out.) differ. Keep it that
// way: logo-render-parity.mjs compares the two statement by statement.
//
// Two WebGPU-only lines, both deliberate:
//   - pos.z remap: uProj is gl-matrix's GL projection (clip z in [-w,w]); WebGPU clips z to
//     [0,w]. Remapping here keeps ONE projection matrix for both tiers, and the depth it
//     writes is exactly the window depth GL writes, (z/w+1)/2.
//   - the fragment returns alpha 1.0 with colour unscaled, which IS premultiplied; the
//     canvas is alphaMode:'premultiplied', so MSAA-resolved edge pixels (colour and alpha
//     both scaled by coverage) composite correctly over the page and the CSS glow.
export const LOGO_WGSL=`
struct Frame
	{
	uProj : mat4x4<f32>,
	uLightDir : vec3<f32>,
	uAmbient : f32,
	uLightColor : vec3<f32>,
	uSpecStrength : f32,
	uPointLightPosObj : vec3<f32>,
	uShininess : f32,
	uPointLightColor : vec3<f32>,
	uPointLightIntensity : f32,
	uPointLightFalloff : f32,
	}

struct Unit
	{
	uModelView : mat4x4<f32>,
	uNormalMatrix : mat3x3<f32>,
	}

@group(0) @binding(0) var<uniform> f : Frame;
@group(0) @binding(1) var<uniform> u : Unit;

struct VSIn
	{
	@location(0) aPos : vec3<f32>,
	@location(1) aNormal : vec3<f32>,
	@location(2) aColor : vec3<f32>,
	}

struct VSOut
	{
	@builtin(position) pos : vec4<f32>,
	@location(0) vNormal : vec3<f32>,
	@location(1) vColor : vec3<f32>,
	@location(2) vPosView : vec3<f32>,
	@location(3) vPLPosView : vec3<f32>,
	}

@vertex
fn vs(in : VSIn) -> VSOut
	{
	var out : VSOut;
	let viewPos=u.uModelView*vec4(in.aPos,1.0);
	out.vPosView=viewPos.xyz;
	out.vNormal=u.uNormalMatrix*in.aNormal;
	out.vColor=in.aColor;
	out.vPLPosView=(u.uModelView*vec4(f.uPointLightPosObj,1.0)).xyz;
	out.pos=f.uProj*viewPos;
	out.pos.z=0.5*(out.pos.z+out.pos.w);
	return out;
	}

@fragment
fn fs(in : VSOut) -> @location(0) vec4<f32>
	{
	let N=normalize(in.vNormal);
	let L=normalize(f.uLightDir);
	let lambert=max(dot(N,L),0.0);
	let diffuse=(1.0-f.uAmbient)*lambert;
	let V=normalize(-in.vPosView);
	let H=normalize(L+V);
	let spec=pow(max(dot(N,H),0.0),f.uShininess)*f.uSpecStrength;
	var color=in.vColor*(f.uAmbient+diffuse*f.uLightColor)+vec3(spec)*f.uLightColor;
	let toLight=in.vPLPosView-in.vPosView;
	let dist=length(toLight);
	let PL=toLight/max(dist,0.0001);
	let pLambert=max(dot(N,PL),0.0);
	let falloff=1.0/(1.0+dist*dist*f.uPointLightFalloff);
	let H2=normalize(PL+V);
	let pSpec=pow(max(dot(N,H2),0.0),f.uShininess)*f.uSpecStrength;
	color+=(in.vColor*pLambert+vec3(pSpec))*f.uPointLightColor*falloff*f.uPointLightIntensity;
	return vec4(color,1.0);
	}
`;

/* ---------- Lighting: one source for both tiers ---------- */

// The renderer's default lighting. Values are user-tuned via the ?debug=light HUD; mutate a
// renderer's `lighting` to change them at runtime. lightColor defaults to white (1,1,1) so
// pages that don't touch it render byte-identical to the pre-coloured-light shader.
// pointLightIntensity defaults to 0 so the optional point light is off everywhere unless a
// page (about/forums) deliberately enables it.
export const LOGO_LIGHTING_DEFAULTS=Object.freeze(
	{
	lx:-1.0,ly:0.76,lz:-1.0,ambient:0.23,specStrength:0.30,shininess:128,
	lightColor:Object.freeze([1.0,1.0,1.0]),
	pointLightPos:Object.freeze([0,0,0]),
	pointLightColor:Object.freeze([1.0,0.1,0.1]),
	pointLightIntensity:0,
	pointLightFalloff:0.00005,
	});

// NewLighting() - a renderer's own mutable copy (arrays copied too: animators replace or
// mutate them per frame).
export function NewLighting()
	{
	const out={};
	for (const [k,v] of Object.entries(LOGO_LIGHTING_DEFAULTS)) out[k]=Array.isArray(v)?v.slice():v;
	return(out);
	}

// Which uniform each lighting field drives. A page preset can only set a field listed here;
// the parity check holds every caller's preset to it.
export const LIGHTING_TO_UNIFORM=Object.freeze(
	{
	lx:'uLightDir',ly:'uLightDir',lz:'uLightDir',
	ambient:'uAmbient',specStrength:'uSpecStrength',shininess:'uShininess',
	lightColor:'uLightColor',
	pointLightPos:'uPointLightPosObj',pointLightColor:'uPointLightColor',
	pointLightIntensity:'uPointLightIntensity',pointLightFalloff:'uPointLightFalloff',
	});

// The uniforms, in WGSL struct order. `wgsl` is the member type in LOGO_WGSL's struct,
// `glsl` the uniform type in the GLSL. One table drives the WebGL uniform calls AND the
// WebGPU buffer packing.
export const FRAME_FIELDS=Object.freeze([
	{name:'uProj',               wgsl:'mat4x4<f32>',glsl:'mat4'},
	{name:'uLightDir',           wgsl:'vec3<f32>',  glsl:'vec3'},
	{name:'uAmbient',            wgsl:'f32',        glsl:'float'},
	{name:'uLightColor',         wgsl:'vec3<f32>',  glsl:'vec3'},
	{name:'uSpecStrength',       wgsl:'f32',        glsl:'float'},
	{name:'uPointLightPosObj',   wgsl:'vec3<f32>',  glsl:'vec3'},
	{name:'uShininess',          wgsl:'f32',        glsl:'float'},
	{name:'uPointLightColor',    wgsl:'vec3<f32>',  glsl:'vec3'},
	{name:'uPointLightIntensity',wgsl:'f32',        glsl:'float'},
	{name:'uPointLightFalloff',  wgsl:'f32',        glsl:'float'},
	]);
export const UNIT_FIELDS=Object.freeze([
	{name:'uModelView',   wgsl:'mat4x4<f32>',glsl:'mat4'},
	{name:'uNormalMatrix',wgsl:'mat3x3<f32>',glsl:'mat3'},
	]);

// FrameValues(L, proj, out) -> {uniformName: value} for FRAME_FIELDS. The guards are the
// pre-WebGPU renderer's: an undefined colour would have crashed uniform3f.
export function FrameValues(L,proj,out)
	{
	out=out||{};
	const ll=Math.hypot(L.lx,L.ly,L.lz)||1;
	const dir=out.uLightDir||(out.uLightDir=[0,0,0]);
	dir[0]=L.lx/ll; dir[1]=L.ly/ll; dir[2]=L.lz/ll;
	out.uProj=proj;
	out.uLightColor=L.lightColor||[1,1,1];
	out.uAmbient=L.ambient;
	out.uSpecStrength=L.specStrength;
	out.uShininess=L.shininess;
	out.uPointLightPosObj=L.pointLightPos||[0,0,0];					// point light: object-space position
	out.uPointLightColor=L.pointLightColor||[1,0,0];				// colour (red default since that's the use case)
	out.uPointLightIntensity=L.pointLightIntensity||0;				// 0 = off, contribution multiplied to zero in shader
	out.uPointLightFalloff=L.pointLightFalloff!=null?L.pointLightFalloff:0.00005;	// inverse-square coefficient; tuned per-mesh
	return(out);
	}

/* ---------- Uniform buffer layout (WebGPU) ---------- */

// WGSL host-shareable sizes and alignments for the member types used above.
const WGSL_TYPE=Object.freeze(
	{
	'f32':        {size:4, align:4},
	'vec3<f32>':  {size:12,align:16},
	'mat3x3<f32>':{size:48,align:16},
	'mat4x4<f32>':{size:64,align:16},
	});

function Layout(fields)
	{
	let off=0,align=1;
	const out=fields.map((fd)=>
		{
		const t=WGSL_TYPE[fd.wgsl];
		off=Math.ceil(off/t.align)*t.align;
		const at={name:fd.name,type:fd.wgsl,offset:off};
		off+=t.size;
		align=Math.max(align,t.align);
		return(at);
		});
	return(Object.freeze({fields:out,size:Math.ceil(off/align)*align}));
	}

export const FRAME_LAYOUT=Layout(FRAME_FIELDS);
export const UNIT_LAYOUT=Layout(UNIT_FIELDS);

// Pack(f32, base, layout, values) - write `values` into f32 starting at float index `base`.
// mat3x3 columns are 16 bytes apart in WGSL; GL's mat3 Float32Array(9) is packed
// column-major, so each column of 3 lands at the start of its 4-float slot.
export function Pack(f32,base,layout,values)
	{
	for (const fd of layout.fields)
		{
		const v=values[fd.name];
		const at=base+fd.offset/4;
		switch (fd.type)
			{
			case 'f32': f32[at]=v; break;
			case 'vec3<f32>': f32[at]=v[0]; f32[at+1]=v[1]; f32[at+2]=v[2]; break;
			case 'mat4x4<f32>': for (let k=0;k<16;k++) f32[at+k]=v[k]; break;
			case 'mat3x3<f32>': for (let c=0;c<3;c++) for (let r=0;r<3;r++) f32[at+c*4+r]=v[c*3+r]; break;
			}
		}
	}

/* ---------- WebGL backend (the fallback tier) ---------- */

function MakeShader(gl,type,src)
	{
	const s=gl.createShader(type);
	gl.shaderSource(s,src);
	gl.compileShader(s);
	if (!gl.getShaderParameter(s,gl.COMPILE_STATUS))
		{
		const log=gl.getShaderInfoLog(s);
		gl.deleteShader(s);
		throw new Error(`shader compile: ${log}`);
		}
	return s;
	}

function MakeProgram(gl,vsrc,fsrc)
	{
	const vs=MakeShader(gl,gl.VERTEX_SHADER,vsrc);
	const fs=MakeShader(gl,gl.FRAGMENT_SHADER,fsrc);
	const p=gl.createProgram();
	gl.attachShader(p,vs);
	gl.attachShader(p,fs);
	gl.linkProgram(p);
	if (!gl.getProgramParameter(p,gl.LINK_STATUS))
		{
		const log=gl.getProgramInfoLog(p);
		throw new Error(`program link: ${log}`);
		}
	return p;
	}

function MakeBuffer(gl,data,target)
	{
	target=target||gl.ARRAY_BUFFER;
	const b=gl.createBuffer();
	gl.bindBuffer(target,b);
	gl.bufferData(target,data,gl.STATIC_DRAW);
	return b;
	}

// SetUniform() - one GL call per GLSL type in FRAME_FIELDS / UNIT_FIELDS.
function SetUniform(gl,loc,glsl,v)
	{
	switch (glsl)
		{
		case 'mat4': gl.uniformMatrix4fv(loc,false,v); break;
		case 'mat3': gl.uniformMatrix3fv(loc,false,v); break;
		case 'vec3': gl.uniform3f(loc,v[0],v[1],v[2]); break;
		case 'float': gl.uniform1f(loc,v); break;
		}
	}

// CreateWebGLBackend(gl, mesh) - the pre-WebGPU renderer's GL code, unchanged in what it
// draws: one program, four static buffers, drawElements per OBJ object range.
export function CreateWebGLBackend(gl,mesh)
	{
	// Uint32 indices are core in WebGL2 but an EXTENSION in WebGL1, and this is a WebGL1
	// context. BuildMesh only makes them past 65,535 vertices (no logo mesh is that big
	// today), and without the extension that draw would fail on every frame, silently.
	if (mesh.indices instanceof Uint32Array && !gl.getExtension('OES_element_index_uint'))
		throw new Error(`mesh has ${mesh.N} vertices, so it needs 32-bit indices, and this WebGL lacks OES_element_index_uint`);

	const program=MakeProgram(gl,LOGO_GLSL_VS,LOGO_GLSL_FS);
	const aPos=gl.getAttribLocation(program,'aPos');
	const aNormal=gl.getAttribLocation(program,'aNormal');
	const aColor=gl.getAttribLocation(program,'aColor');
	const loc={};
	for (const fd of [...FRAME_FIELDS,...UNIT_FIELDS]) loc[fd.name]=gl.getUniformLocation(program,fd.name);
	const posBuf=MakeBuffer(gl,mesh.positions);
	const normBuf=MakeBuffer(gl,mesh.normals);
	const colorBuf=MakeBuffer(gl,mesh.colors);
	const idxBuf=MakeBuffer(gl,mesh.indices,gl.ELEMENT_ARRAY_BUFFER);
	const indexType=mesh.indices instanceof Uint16Array?gl.UNSIGNED_SHORT:gl.UNSIGNED_INT;
	const indexBytes=indexType===gl.UNSIGNED_SHORT?2:4;

	gl.enable(gl.DEPTH_TEST);
	gl.depthFunc(gl.LEQUAL);
	gl.clearColor(0,0,0,0);											// transparent — body bg shows through

	return({
		tier:'webgl',
		resize(W,H) { gl.viewport(0,0,W,H); },
		begin(frame)
			{
			gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);
			gl.useProgram(program);
			for (const fd of FRAME_FIELDS) SetUniform(gl,loc[fd.name],fd.glsl,frame[fd.name]);
			gl.bindBuffer(gl.ARRAY_BUFFER,posBuf);
			gl.enableVertexAttribArray(aPos);
			gl.vertexAttribPointer(aPos,3,gl.FLOAT,false,0,0);
			gl.bindBuffer(gl.ARRAY_BUFFER,normBuf);
			gl.enableVertexAttribArray(aNormal);
			gl.vertexAttribPointer(aNormal,3,gl.FLOAT,false,0,0);
			gl.bindBuffer(gl.ARRAY_BUFFER,colorBuf);
			gl.enableVertexAttribArray(aColor);
			gl.vertexAttribPointer(aColor,3,gl.FLOAT,false,0,0);
			gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER,idxBuf);
			},
		unit(ui,mv,nm,ranges)
			{
			gl.uniformMatrix4fv(loc.uModelView,false,mv);
			gl.uniformMatrix3fv(loc.uNormalMatrix,false,nm);
			// One drawElements per OBJ object in the unit. All units share the
			// same vertex buffers; only the index range changes.
			for (const o of ranges) gl.drawElements(gl.TRIANGLES,o.indexCount,indexType,o.indexStart*indexBytes);
			},
		end() {},
		dispose()
			{
			for (const b of [posBuf,normBuf,colorBuf,idxBuf]) gl.deleteBuffer(b);
			gl.deleteProgram(program);
			},
		});
	}

/* ---------- WebGPU backend (the first tier) ---------- */

export const LOGO_MSAA_SAMPLES=4;									// WebGL's antialias:true is 4x in every browser we target; WebGPU allows 1 or 4
export const LOGO_DEPTH_FORMAT='depth24plus';

// CreateWebGPUBackend(context, {device, format}, mesh, unitCount) -> Promise<backend>
// `context` is the canvas's GPUCanvasContext (KAN_Logo3D creates it). One pipeline; every
// unit's uniforms live in ONE buffer, a ring of 256-byte slots - slot 0 the frame, slot 1+i
// unit i - read through a dynamic offset, so a frame is one writeBuffer and one pass.
//
// Rejects on ANY setup failure - a WGSL compile error, a pipeline or buffer the device
// refuses - because WebGPU reports those through error scopes, not exceptions, and an
// invalid pipeline draws nothing without a word: a logo that is "up" and invisible. The
// caller falls through to the WebGL tier instead. A validation error on the FIRST frame is
// reported to backend.onFatal (set it), for the same reason.
export async function CreateWebGPUBackend(context,gpu,mesh,unitCount)
	{
	const {device,format}=gpu;
	const made=[];													// every buffer and texture, destroyed by dispose()
	const Track=(o)=>{ made.push(o); return(o); };

	device.pushErrorScope('out-of-memory');
	device.pushErrorScope('validation');
	let parts=null, thrown=null;
	try
		{
		context.configure({device,format,alphaMode:'premultiplied'});
		const module=device.createShaderModule({label:'logo3d',code:LOGO_WGSL});
		if (typeof module.getCompilationInfo==='function')
			{
			const info=await module.getCompilationInfo();
			const errs=(info.messages||[]).filter((m)=>m.type==='error');
			if (errs.length) throw new Error('logo WGSL: '+errs.map((m)=>`${m.lineNum}:${m.linePos} ${m.message}`).join('; '));
			}
		const layout=device.createBindGroupLayout(
			{
			label:'logo3d',
			entries:
				[
				{binding:0,visibility:GPUShaderStage.VERTEX|GPUShaderStage.FRAGMENT,buffer:{type:'uniform',minBindingSize:FRAME_LAYOUT.size}},
				{binding:1,visibility:GPUShaderStage.VERTEX,buffer:{type:'uniform',hasDynamicOffset:true,minBindingSize:UNIT_LAYOUT.size}},
				],
			});
		const vbuf=(loc)=>({arrayStride:12,attributes:[{shaderLocation:loc,offset:0,format:'float32x3'}]});
		const pipeline=await device.createRenderPipelineAsync(
			{
			label:'logo3d',
			layout:device.createPipelineLayout({bindGroupLayouts:[layout]}),
			vertex:{module,entryPoint:'vs',buffers:[vbuf(0),vbuf(1),vbuf(2)]},
			fragment:{module,entryPoint:'fs',targets:[{format}]},
			primitive:{topology:'triangle-list',cullMode:'none'},		// GL never enabled CULL_FACE
			depthStencil:{format:LOGO_DEPTH_FORMAT,depthWriteEnabled:true,depthCompare:'less-equal'},	// GL: DEPTH_TEST + LEQUAL
			multisample:{count:LOGO_MSAA_SAMPLES},
			});

		const align=(device.limits && device.limits.minUniformBufferOffsetAlignment)||256;
		const stride=Math.ceil(Math.max(FRAME_LAYOUT.size,UNIT_LAYOUT.size)/align)*align;
		const slots=1+Math.max(1,unitCount|0);
		const ubo=Track(device.createBuffer({label:'logo3d uniforms',size:stride*slots,usage:GPUBufferUsage.UNIFORM|GPUBufferUsage.COPY_DST}));
		const bindGroup=device.createBindGroup(
			{
			label:'logo3d',
			layout,
			entries:
				[
				{binding:0,resource:{buffer:ubo,offset:0,size:FRAME_LAYOUT.size}},
				{binding:1,resource:{buffer:ubo,offset:0,size:UNIT_LAYOUT.size}},
				],
			});
		const Upload=(data,usage)=>
			{
			const b=Track(device.createBuffer({size:data.byteLength,usage:usage|GPUBufferUsage.COPY_DST}));
			device.queue.writeBuffer(b,0,data);
			return(b);
			};
		parts=
			{
			pipeline,bindGroup,ubo,stride,slots,
			pos:Upload(mesh.positions,GPUBufferUsage.VERTEX),
			norm:Upload(mesh.normals,GPUBufferUsage.VERTEX),
			color:Upload(mesh.colors,GPUBufferUsage.VERTEX),
			idx:Upload(new Uint32Array(mesh.indices),GPUBufferUsage.INDEX),	// always 32-bit: native in WebGPU, and a Uint16Array of odd length is not a 4-byte-multiple write
			};
		}
	catch (err)
		{
		thrown=err;
		}
	const invalid=await device.popErrorScope();
	const oom=await device.popErrorScope();
	if (thrown||invalid||oom)
		{
		for (const o of made) { try { o.destroy(); } catch (err) { /* already gone */ } }
		try { context.unconfigure(); } catch (err) { /* never configured */ }
		throw(thrown||new Error(`logo WebGPU setup: ${(invalid||oom).message}`));
		}

	const {pipeline,bindGroup,ubo,stride,slots}=parts;
	const staging=new Float32Array(stride*slots/4);
	const draws=[];													// [ui, ranges] for this frame
	let msaa=null,depth=null,msaaView=null,depthView=null;
	let firstFrame=true;

	const backend=
		{
		tier:'webgpu',
		maxDim:(device.limits && device.limits.maxTextureDimension2D)||8192,
		onFatal:null,
		resize(W,H)
			{
			if (msaa) msaa.destroy();
			if (depth) depth.destroy();
			msaa=device.createTexture({label:'logo3d msaa',size:[W,H],format,sampleCount:LOGO_MSAA_SAMPLES,usage:GPUTextureUsage.RENDER_ATTACHMENT});
			depth=device.createTexture({label:'logo3d depth',size:[W,H],format:LOGO_DEPTH_FORMAT,sampleCount:LOGO_MSAA_SAMPLES,usage:GPUTextureUsage.RENDER_ATTACHMENT});
			msaaView=msaa.createView();
			depthView=depth.createView();
			},
		begin(frame)
			{
			Pack(staging,0,FRAME_LAYOUT,frame);
			draws.length=0;
			},
		unit(ui,mv,nm,ranges)
			{
			if (ui+1>=slots) return;									// more units than the ring was built for: cannot happen (unitCount is units.length)
			Pack(staging,(1+ui)*stride/4,UNIT_LAYOUT,{uModelView:mv,uNormalMatrix:nm});
			draws.push(ui,ranges);
			},
		end()
			{
			if (!msaaView) return;									// no backing store yet (zero-size window)
			let used=1;
			for (let k=0;k<draws.length;k+=2) used=Math.max(used,draws[k]+2);
			const check=firstFrame;
			firstFrame=false;
			if (check) device.pushErrorScope('validation');
			device.queue.writeBuffer(ubo,0,staging,0,used*stride/4);
			const enc=device.createCommandEncoder({label:'logo3d'});
			const pass=enc.beginRenderPass(
				{
				colorAttachments:
					[{
					view:msaaView,
					resolveTarget:context.getCurrentTexture().createView(),
					clearValue:{r:0,g:0,b:0,a:0},						// transparent — body bg shows through
					loadOp:'clear',
					storeOp:'discard',									// the resolve still happens; only the 4x samples are dropped
					}],
				depthStencilAttachment:{view:depthView,depthClearValue:1,depthLoadOp:'clear',depthStoreOp:'discard'},
				});
			pass.setPipeline(pipeline);
			pass.setVertexBuffer(0,parts.pos);
			pass.setVertexBuffer(1,parts.norm);
			pass.setVertexBuffer(2,parts.color);
			pass.setIndexBuffer(parts.idx,'uint32');
			for (let k=0;k<draws.length;k+=2)
				{
				pass.setBindGroup(0,bindGroup,[(1+draws[k])*stride]);
				for (const o of draws[k+1]) pass.drawIndexed(o.indexCount,1,o.indexStart,0,0);
				}
			pass.end();
			device.queue.submit([enc.finish()]);
			if (check) device.popErrorScope().then((err)=>{ if (err && backend.onFatal) backend.onFatal(err); },()=>{});
			},
		dispose()
			{
			for (const o of [...made,msaa,depth]) { if (o) { try { o.destroy(); } catch (err) { /* already gone */ } } }
			msaa=depth=msaaView=depthView=null;
			try { context.unconfigure(); } catch (err) { /* device already gone */ }
			},
		};
	return(backend);
	}
